import React, { useEffect, useState } from "react";
import { Alert, Card, Table, ButtonGroup, Button, Modal } from "react-bootstrap";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import Swal from "sweetalert2";

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

const StudentAvatar = ({ photo, name, fill = false }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  if (fill) {
    return (
      <>
        <div
          style={{ width: '100%', height: '100%', cursor: src ? 'pointer' : 'default' }}
          onClick={() => src && setShowModal(true)}
        >
          {src
            ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', backgroundColor: '#adb5bd', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 600 }}>{initial}</div>
          }
        </div>
        {src && (
          <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
            <Modal.Body className="p-3 text-center">
              <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
              {name && <div className="mt-2 fw-semibold small">{name}</div>}
            </Modal.Body>
          </Modal>
        )}
      </>
    );
  }

  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

// ตรวจจับหน้าจอมือถือ (max-width: 768px)
const useIsMobile = (query = '(max-width: 768px)') => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return isMobile;
};

export default function IslamicCheckAttendance() {
  const { ir_id, is_teachId } = useParams();
  const location  = useLocation();
  const navigate  = useNavigate();
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const STATUS_OPTIONS = [
    { value: "present", label: t.checkAttendance.statusPresent, color: "success" },
    { value: "late",    label: t.checkAttendance.statusLate,    color: "warning" },
    { value: "leave",   label: t.checkAttendance.statusLeave,   color: "info"    },
    { value: "absent",  label: t.checkAttendance.statusAbsent,  color: "danger"  },
  ];

  // ปุ่มเลือกสถานะการเข้าเรียน (ใช้ร่วมกันทั้งตารางและการ์ด)
  const AttendanceButtons = ({ index, status, size = 'sm' }) => (
    <ButtonGroup size={size}>
      {STATUS_OPTIONS.map(opt => (
        <Button
          key={opt.value}
          variant={status === opt.value ? opt.color : `outline-${opt.color}`}
          onClick={() => handleStatusChange(index, opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  const isMobile = useIsMobile();
  // โหมดแสดงผล: บนมือถือเริ่มต้นเป็น 'card', บนจอใหญ่เป็น 'table' (ผู้ใช้สลับเองได้)
  const [viewMode, setViewMode] = useState(null);
  const effectiveView = viewMode ?? (isMobile ? 'card' : 'table');

  const periods = location.state?.periods || [];

  useEffect(() => {
    if (periods.length === 0) navigate('/islamic/my-subjects', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const today = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [topic, setTopic]               = useState("");
  const [data, setData]                 = useState([]);
  const [teaching, setTeaching]         = useState(null);
  const [alreadyChecked, setAlreadyChecked] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // กันกดปุ่มบันทึกรัว ๆ

  const attendanceKey = `is_att_${is_teachId}_${selectedDate}_${periods.join(',')}`;

  const getSavedAttendance = React.useCallback(() => {
    const saved = localStorage.getItem(attendanceKey);
    return saved ? JSON.parse(saved) : null;
  }, [attendanceKey]);

  useEffect(() => {
    if (!is_teachId) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/mySubjects/${
      JSON.parse(sessionStorage.getItem('user') || '{}').t_id || 0
    }/${localStorage.getItem("yearId") || 0}`)
      .then(res => {
        const found = res.data.find(s => String(s.is_teachId) === String(is_teachId));
        setTeaching(found || null);
      })
      .catch(() => {});
  }, [is_teachId]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic/getStudentsInRoom/${ir_id}`)
      .then(res => {
        const savedData = getSavedAttendance();
        if (savedData) {
          setData(savedData);
        } else {
          setData(res.data.map(s => ({
            ...s,
            is_att_status: s.st_status === '0' ? 'absent' : 'present',
          })));
        }
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [ir_id, getSavedAttendance, setLoading]);

  useEffect(() => {
    if (data.length > 0) localStorage.setItem(attendanceKey, JSON.stringify(data));
  }, [data, attendanceKey]);

  useEffect(() => {
    if (!is_teachId || !selectedDate || periods.length === 0) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/attendanceForEdit/${is_teachId}/${selectedDate}/${periods[0]}`)
      .then(res => setAlreadyChecked(res.data && res.data.length > 0))
      .catch(() => setAlreadyChecked(false));
  }, [is_teachId, selectedDate, periods]);

  const handleStatusChange = (index, status) => {
    setData(prev => {
      const next = [...prev];
      next[index] = { ...next[index], is_att_status: status };
      return next;
    });
  };

  const handleSave = () => {
    if (isSaving) return; // กันการยิงซ้ำระหว่างที่ request ก่อนหน้ายังไม่ตอบ
    if (!topic.trim()) {
      document.getElementById('topicInput')?.classList.add('is-invalid');
      document.getElementById('topicInput')?.focus();
      return;
    }
    setIsSaving(true);
    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/islamic-teaching/saveAttendance`, {
      is_teachId: Number(is_teachId),
      date:       selectedDate,
      periods,
      topic,
      records: data.map(s => ({ is_study_id: s.is_study_id, is_att_status: s.is_att_status })),
    })
      .then(() => {
        localStorage.removeItem(attendanceKey);
        Swal.fire({ icon: 'success', title: t.checkAttendance.saveSuccess, timer: 1500, showConfirmButton: false })
          .then(() => navigate(`/islamic/my-subjects`));
      })
      .catch(err => {
        localStorage.removeItem(attendanceKey);
        Swal.fire({
          icon: 'error',
          title: err.response?.data?.title || t.checkAttendance.errorTitle,
          text:  err.response?.data?.message || t.common.error,
        });
      })
      .finally(() => {
        setLoading(false);
        setIsSaving(false);
      });
  };

  const roomLabel = teaching ? `${teaching.ic_class}/${teaching.ir_no}` : `—`;
  const subLabel  = teaching ? `${teaching.isubject_isid} ${teaching.isubject_name}` : '';

  return (
    <React.Fragment>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">
              <Link to="/islamic/attendance">{t.checkAttendance.breadcrumb}</Link>
            </li>
            <li className="breadcrumb-item active">{t.common.classNo('', roomLabel)}</li>
          </ol>
          <h4 className="main-title mb-0">
            {t.checkAttendance.pageTitle} {roomLabel}
            {subLabel && <span className="text-secondary fs-6 ms-2 arabic-text">{subLabel}</span>}
          </h4>
        </div>
      </div>

      {alreadyChecked && (
        <Alert variant="warning" className="d-flex align-items-center gap-2 mb-3">
          <i className="ri-error-warning-line fs-5"></i>
          <span>
            {t.checkAttendance.alreadyChecked} —
            <Link to={`/islamic/edit-attendance/${is_teachId}/${selectedDate}/${periods[0]}`} className="ms-1 text-decoration-underline">
              {t.checkAttendance.editLink}
            </Link>
          </span>
        </Alert>
      )}

      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <label className="mb-0 fw-semibold">{t.checkAttendance.labelDate}</label>
          <input
            type="date"
            className="form-control"
            style={{ maxWidth: 180 }}
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
          />
        </div>
        <div className="d-flex align-items-center gap-2 flex-grow-1">
          <label className="mb-0 fw-semibold" style={{ whiteSpace: 'nowrap' }}>{t.checkAttendance.labelTopic}</label>
          <input
            id="topicInput"
            type="text"
            className="form-control"
            placeholder={t.checkAttendance.topicPlaceholder}
            value={topic}
            onChange={e => { setTopic(e.target.value); document.getElementById('topicInput')?.classList.remove('is-invalid'); }}
          />
        </div>
      </div>

      <Card className="card-one">
        <Card.Body className="p-3">
          {/* ปุ่มสลับมุมมอง ตาราง / การ์ด */}
          <div className="d-flex justify-content-end mb-3">
            <ButtonGroup size="sm">
              <Button
                variant={effectiveView === 'card' ? 'primary' : 'outline-primary'}
                onClick={() => setViewMode('card')}
                title={t.checkAttendance.viewCard}
              >
                <i className="ri-layout-grid-line"></i> {t.checkAttendance.viewCard}
              </Button>
              <Button
                variant={effectiveView === 'table' ? 'primary' : 'outline-primary'}
                onClick={() => setViewMode('table')}
                title={t.checkAttendance.viewTable}
              >
                <i className="ri-table-line"></i> {t.checkAttendance.viewTable}
              </Button>
            </ButtonGroup>
          </div>

          {effectiveView === 'table' ? (
            <Table hover responsive className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 50 }} className="text-center">#</th>
                  <th style={{ width: 110 }}>{t.checkAttendance.colId}</th>
                  <th>{t.checkAttendance.colName}</th>
                  <th className="text-center" style={{ width: 280 }}>{t.checkAttendance.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-secondary py-4">{t.checkAttendance.noStudents}</td></tr>
                ) : data.map((s, idx) => (
                  <tr key={s.is_study_id} className={s.st_status === '0' ? 'table-danger' : ''}>
                    <td className="text-center align-middle text-secondary">{idx + 1}</td>
                    <td className="align-middle">{s.st_idnumber}</td>
                    <td className="align-middle">
                      <div className="d-flex align-items-center gap-2">
                        <StudentAvatar photo={s.st_photo} name={s.st_name} />
                        <span>{s.st_name}</span>
                      </div>
                    </td>
                    <td className="text-center align-middle">
                      {s.st_status !== '0' ? (
                        <AttendanceButtons index={idx} status={s.is_att_status} />
                      ) : (
                        <span className="text-danger small">{t.checkAttendance.statusWithdrawn}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="d-flex flex-column gap-2">
              {data.length === 0 ? (
                <div className="text-center text-secondary py-3">{t.checkAttendance.noStudents}</div>
              ) : data.map((s, idx) => (
                <Card
                  key={s.is_study_id}
                  className={`border ${s.st_status === '0' ? 'border-danger bg-danger bg-opacity-10' : ''}`}
                >
                  <Card.Body className="p-0" style={{ minHeight: 76 }}>
                    <div className="d-flex" style={{ minHeight: 76 }}>
                      {/* รูปใหญ่ชิดซ้าย */}
                      <div style={{ width: 64, flexShrink: 0, overflow: 'hidden', borderRadius: 'calc(0.375rem - 1px) 0 0 calc(0.375rem - 1px)' }}>
                        <StudentAvatar photo={s.st_photo} name={s.st_name} fill />
                      </div>
                      {/* ข้อมูล + ปุ่ม */}
                      <div className="flex-grow-1 px-2 py-2 d-flex flex-column justify-content-between">
                        <div className="d-flex align-items-start gap-2">
                          <span className="badge bg-secondary flex-shrink-0 mt-1">{idx + 1}</span>
                          <div className="flex-grow-1 text-end">
                            <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{s.st_name}</div>
                            <small className="text-muted">{t.checkAttendance.colId} {s.st_idnumber}</small>
                          </div>
                        </div>
                        <div className="d-flex justify-content-end">
                          {s.st_status !== '0' ? (
                            <AttendanceButtons index={idx} status={s.is_att_status} />
                          ) : (
                            <span className="text-danger small">{t.checkAttendance.statusWithdrawn}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
        {data.length > 0 && (
          <Card.Footer className="d-flex justify-content-end">
            <Button variant="success" onClick={handleSave} disabled={isSaving}>
              <i className="ri-save-3-fill me-1"></i>{t.checkAttendance.saveBtn}
            </Button>
          </Card.Footer>
        )}
      </Card>
    </React.Fragment>
  );
}
